// CHAT-SWITCH-A (docs/direction/2026-10-28-chat-slack-switch.md, wave 1): what a team needs
// before it leaves Slack, first group.
//
//   channels.files         the Files tab: every file sent in a channel the caller can read, newest
//                          first, by kind and by who shared it; each opens its message.
//   chatStatus.list        everyone's presence (active / away, from their open sessions), custom
//                          status (emoji, text, until), local time zone and do-not-disturb (their
//                          quiet hours now). Company members see the company's people; a client
//                          viewer sees the people of the channels it is in.
//   chatStatus.set         the caller's own status and time zone.
//   chatReminders.*        "Remind me about this" and `/remind`: a reminder of the caller's own
//                          (the `reminder` table, `membership_id`), fired into their inbox by
//                          `reminders.fire`. A message reminder opens its message, while the
//                          caller can still read it.
//
// `@channel` / `@here` ride on `messages.create` (`broadcast`); the search filters on
// `search.query` (`from`, `in`, `has`, `before`, `after`, `is`).
import { z } from "zod";
import { Timestamp, Uuid } from "../primitives";
import { ChatAttachment } from "./chat-files";
import { CompanyItemParams, CompanyParams, PageQuery, page } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const chat = { feature: "chat" } as const;

// Files tab ------------------------------------------------------------------------------------

/** The Files tab's kind filter (by MIME type). */
export const ChannelFileKind = z.enum([
  "image",
  "pdf",
  "document",
  "spreadsheet",
  "media",
  "other",
]);
export type ChannelFileKind = z.infer<typeof ChannelFileKind>;

export const ChannelFilesQuery = PageQuery.extend({
  kind: ChannelFileKind.optional(),
  /** Only the files this member shared. */
  from: Uuid.optional(),
}).meta({ id: "ChannelFilesQuery" });

/** A file of the channel, with where its message is (the topic, and the thread root of a reply). */
export const ChannelFile = ChatAttachment.extend({
  kind: ChannelFileKind,
  topic_id: Uuid,
  /** The root message when the file is on a thread reply (null on a root message). */
  parent_message_id: Uuid.nullable(),
}).meta({ id: "ChannelFile" });
export type ChannelFile = z.infer<typeof ChannelFile>;

// Status and presence ---------------------------------------------------------------------------

/** `active`: one of the person's sessions is open and in use now; otherwise `away`. */
export const ChatPresence = z.enum(["active", "away"]);

export const ChatStatus = z
  .object({
    membership_id: Uuid,
    presence: ChatPresence,
    /** The custom status (null when none is set or it has expired). */
    emoji: z.string().nullable(),
    text: z.string().nullable(),
    /** When the custom status clears itself (null: until changed). */
    expires_at: Timestamp.nullable(),
    /** IANA time zone: the one the person's browser reports, else their quiet-hours zone. */
    time_zone: z.string().nullable(),
    /** Do not disturb: the person's quiet hours are on now (no desktop or push notifications). */
    dnd: z.boolean(),
  })
  .meta({ id: "ChatStatus" });
export type ChatStatus = z.infer<typeof ChatStatus>;

export const ChatStatusList = z
  .object({ data: z.array(ChatStatus) })
  .meta({ id: "ChatStatusList" });

/** Fields left out stay as they are; `null` clears one. A new emoji or text keeps the other. */
export const ChatStatusPut = z
  .strictObject({
    emoji: z.string().trim().min(1).max(32).nullable().optional(),
    text: z.string().trim().min(1).max(100).nullable().optional(),
    expires_at: Timestamp.nullable().optional(),
    time_zone: z.string().min(1).max(64).optional(),
  })
  .meta({ id: "ChatStatusPut" });

// Reminders --------------------------------------------------------------------------------------

/**
 * "Remind me about this" (`thread_id` + `message_id`) or `/remind` (`text`). `at` must be in the
 * future and within a year.
 */
export const ChatReminderCreate = z
  .strictObject({
    at: Timestamp,
    text: z.string().trim().min(1).max(500).optional(),
    thread_id: Uuid.optional(),
    message_id: Uuid.optional(),
  })
  .refine((b) => (b.thread_id === undefined) === (b.message_id === undefined), {
    message: "thread_id and message_id go together",
  })
  .refine((b) => b.message_id !== undefined || b.text !== undefined, {
    message: "a reminder needs a message or a text",
  })
  .meta({ id: "ChatReminderCreate" });

/** One of the caller's reminders, with where its message is while the caller can still read it. */
export const ChatReminder = z
  .object({
    id: Uuid,
    at: Timestamp,
    text: z.string(),
    message_id: Uuid.nullable(),
    thread_id: Uuid.nullable(),
    channel_id: Uuid.nullable(),
    topic_id: Uuid.nullable(),
    parent_message_id: Uuid.nullable(),
    fired_at: Timestamp.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "ChatReminder" });
export type ChatReminder = z.infer<typeof ChatReminder>;

export const ChatReminderList = z
  .object({ data: z.array(ChatReminder) })
  .meta({ id: "ChatReminderList" });

export const chatSwitchRoutes = {
  "channels.files": defineRoute({
    ...chat,
    id: "channels.files",
    method: "get",
    path: `${base}/channels/{id}/files`,
    tag: "channels",
    summary: "The Files tab: every file sent in the channel, newest first (by kind, by sharer)",
    access: "member",
    params: CompanyItemParams,
    query: ChannelFilesQuery,
    response: { status: 200, schema: page(ChannelFile) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "chatStatus.list": defineRoute({
    ...chat,
    id: "chatStatus.list",
    method: "get",
    path: `${base}/chat/statuses`,
    tag: "presence",
    summary: "Presence, custom status, local time zone and do-not-disturb of the people I can see",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: ChatStatusList },
    errors: [],
    emits: [],
  }),
  "chatStatus.set": defineRoute({
    ...chat,
    id: "chatStatus.set",
    method: "put",
    path: `${base}/chat/status`,
    tag: "presence",
    summary: "Set or clear my status (emoji, text, until) or my time zone",
    access: "member",
    params: CompanyParams,
    body: ChatStatusPut,
    response: { status: 200, schema: ChatStatus },
    errors: [],
    emits: ["member_status.changed"],
  }),
  "chatReminders.list": defineRoute({
    ...chat,
    id: "chatReminders.list",
    method: "get",
    path: `${base}/chat/reminders`,
    tag: "reminders",
    summary: "My reminders: open ones soonest first, and fired ones until dismissed (30 days)",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: ChatReminderList },
    errors: [],
    emits: [],
  }),
  "chatReminders.create": defineRoute({
    ...chat,
    id: "chatReminders.create",
    method: "post",
    path: `${base}/chat/reminders`,
    tag: "reminders",
    summary: "Remind me about a message, or of a text (`/remind`), at a time",
    access: "member",
    params: CompanyParams,
    body: ChatReminderCreate,
    response: { status: 201, schema: ChatReminder },
    errors: [],
    emits: ["reminder.created"],
  }),
  "chatReminders.dismiss": defineRoute({
    ...chat,
    id: "chatReminders.dismiss",
    method: "post",
    path: `${base}/chat/reminders/{id}/dismiss`,
    tag: "reminders",
    summary: "Cancel one of my reminders (or clear a fired one)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: ChatReminder },
    errors: [],
    emits: ["reminder.dismissed"],
  }),
};
